import { useEffect, useState } from "react";
import {
  CircleNotch,
  Files,
  Sparkle,
  ArrowSquareOut,
  FilePdf,
  Image as ImageIcon,
  FileDoc,
  FileText,
  Megaphone,
  EnvelopeSimple,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const formatBytes = (b) => {
  if (!b && b !== 0) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const iconFor = (mime) => {
  if (!mime) return FileText;
  if (mime === "application/pdf") return FilePdf;
  if (mime.startsWith("image/")) return ImageIcon;
  if (mime.includes("word")) return FileDoc;
  return FileText;
};

const labelForCategory = (cat) => {
  const c = (cat || "general").toLowerCase();
  return {
    general: "General",
    policy: "Policy",
    marketing: "Marketing",
    "retailer-pack": "For retailers",
    press: "Press",
  }[c] || c.replace(/-/g, " ");
};

export default function Press() {
  const { settings } = useSettings();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/documents/public")
      .then((r) => setDocs(r.data.documents || []))
      .catch(() => setDocs([]))
      .finally(() => setLoading(false));
  }, []);

  // Group by category for nicer browsing
  const grouped = docs.reduce((acc, d) => {
    const k = d.category || "general";
    (acc[k] = acc[k] || []).push(d);
    return acc;
  }, {});
  const categories = Object.keys(grouped).sort();

  const pressEmail = settings?.general_email || "hello@mineai.io";

  return (
    <div data-testid="press-page">
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pt-14 sm:pt-20 pb-10">
        <p className="text-xs uppercase tracking-[0.25em] text-[#3B5BDB] font-semibold flex items-center gap-2">
          <Sparkle size={14} weight="fill" className="text-[#D4AF37]" /> Press &amp; resources
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-4 text-[#0A1128] tracking-tight max-w-3xl leading-[1.05]">
          Mine AI for{" "}
          <span className="gold-text">journalists, retailers &amp; partners</span>.
        </h1>
        <p className="mt-4 text-base sm:text-lg text-[#4A5568] max-w-2xl">
          Brand kit, retailer pack, signed policies, and product PDFs — everything our team has
          made public, in one place. Help yourself, attribute fairly.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-10">
        {loading ? (
          <div className="mc-card p-10 flex items-center justify-center gap-3 text-[#4A5568]" data-testid="press-loading">
            <CircleNotch className="animate-spin" weight="bold" /> Loading resources...
          </div>
        ) : docs.length === 0 ? (
          <div className="mc-card p-10 text-center" data-testid="press-empty">
            <Files size={36} weight="duotone" className="text-[#3B5BDB]/40 mx-auto" />
            <p className="mt-3 font-display text-2xl text-[#0A1128]">Nothing here yet</p>
            <p className="mt-1 text-sm text-[#4A5568] max-w-sm mx-auto">
              We're polishing our brand kit and retailer pack — they'll appear here soon.
              In the meantime, drop us a line for any media or partnership query.
            </p>
            <a
              href={`mailto:${pressEmail}?subject=Mine%20AI%20press%20enquiry`}
              data-testid="press-email-link"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#3B5BDB] text-white font-medium hover:bg-[#2F49AF] smooth"
            >
              <EnvelopeSimple size={16} weight="duotone" /> {pressEmail}
            </a>
          </div>
        ) : (
          <div className="space-y-10">
            {categories.map((cat) => (
              <div key={cat} data-testid={`press-category-${cat}`}>
                <p className="text-[11px] uppercase tracking-[0.2em] text-[#3B5BDB] font-semibold">
                  {labelForCategory(cat)}
                </p>
                <div className="mt-4 grid sm:grid-cols-2 gap-4">
                  {grouped[cat].map((d) => (
                    <DocCard key={d.id} doc={d} />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Press contact */}
      <section className="max-w-5xl mx-auto px-5 sm:px-8 pb-20">
        <div className="mc-card p-7 sm:p-9 bg-gradient-to-br from-[#3B5BDB]/5 to-[#D4AF37]/5">
          <div className="flex items-start gap-4 flex-wrap">
            <Megaphone size={28} weight="duotone" className="text-[#3B5BDB] shrink-0 mt-1" />
            <div className="flex-1 min-w-[260px]">
              <h2 className="font-display text-2xl sm:text-3xl text-[#0A1128]">
                Working on a story or partnership?
              </h2>
              <p className="mt-2 text-sm text-[#4A5568] max-w-2xl">
                Quote us, profile us, or carry Mine AI in your shop — we'd love to help.
                Email us with your deadline and we'll get back within a working day.
              </p>
            </div>
            <a
              href={`mailto:${pressEmail}?subject=Mine%20AI%20press%20%2F%20partnership%20enquiry`}
              data-testid="press-contact-cta"
              className="px-5 py-2.5 rounded-md bg-[#0A1128] text-white font-medium hover:bg-[#0A1128]/90 smooth flex items-center gap-2 shrink-0"
            >
              <EnvelopeSimple size={16} weight="duotone" /> {pressEmail}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

function DocCard({ doc }) {
  const Icon = iconFor(doc.mime_type);
  const base = process.env.REACT_APP_BACKEND_URL;
  // Public docs require no auth, so a plain href works
  const href = `${base}/api/documents/${doc.id}/download`;

  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="mc-card p-5 flex items-start gap-4 lift smooth group"
      data-testid={`press-doc-${doc.id}`}
    >
      <Icon size={32} weight="duotone" className="text-[#3B5BDB] shrink-0 mt-0.5" />
      <div className="min-w-0 flex-1">
        <p className="font-display text-lg text-[#0A1128] truncate">{doc.label}</p>
        <p className="text-xs text-[#4A5568] mt-0.5 truncate">
          {doc.filename} · {formatBytes(doc.size_bytes)}
        </p>
        <p className="text-[11px] text-[#4A5568]/80 mt-1">
          Updated {new Date(doc.created_at).toLocaleDateString()}
        </p>
      </div>
      <ArrowSquareOut
        size={18}
        weight="duotone"
        className="text-[#4A5568]/60 group-hover:text-[#3B5BDB] smooth shrink-0 mt-0.5"
      />
    </a>
  );
}
